import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, EMBEDS } from "../i18n/content";
import { Reveal, Overline, MaskedLines } from "../components/Reveal";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { LazyEmbed } from "../components/LazyEmbed";
import { FinalCTA } from "../components/FinalCTA";
import eventsData from "../data/events.json";
import { upcomingEvents } from "../lib/events";

function Hero() {
  const { t, pathFor } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} className="relative h-screen min-h-[640px] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="DEEJAY performing at sunset by the sea" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/55 via-charcoal/35 to-charcoal/75" />
      </motion.div>

      <motion.div style={{ y: overlayY }} className="relative flex h-full flex-col justify-end px-6 pb-16 lg:px-10 lg:pb-24">
        <div className="mx-auto w-full max-w-7xl">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
            <Overline className="text-gold">{t("hero.overline")}</Overline>
          </motion.div>

          <h1 className="mt-5 font-serif text-[3.25rem] font-light leading-[0.92] tracking-tight text-cream sm:text-7xl lg:text-8xl">
            <MaskedLines
              lines={[t("hero.line1"), t("hero.line2"), t("hero.line3")]}
              delay={0.15}
              lineClassName="italic"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-cream/85 sm:text-lg"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to={pathFor("/contact")}
              data-testid="hero-book-btn"
              className="rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terracotta-deep"
            >
              {t("cta.book")}
            </Link>
            <Link
              to={pathFor("/music")}
              data-testid="hero-listen-btn"
              className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-8 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-cream/10"
            >
              {t("cta.listen")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.1 }}
        className="absolute bottom-8 right-6 hidden items-center gap-2 text-xs uppercase tracking-overline text-cream/70 lg:right-10 lg:flex"
      >
        {t("hero.scroll")}
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
}

function Intro() {
  const { t, pathFor } = useLang();
  return (
    <section className="relative bg-cream py-24 lg:py-36" data-testid="home-intro">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-24 lg:px-10">
        <Reveal className="lg:sticky lg:top-32 lg:self-start">
          <Overline>{t("intro.overline")}</Overline>
          <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-charcoal sm:text-5xl">
            {t("intro.title")}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-xl leading-relaxed text-charcoal/75">{t("intro.body")}</p>
          <Link
            to={pathFor("/about")}
            data-testid="intro-story-link"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-terracotta"
          >
            {t("intro.link")} <ArrowUpRight className="h-4 w-4" />
          </Link>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-border pt-10">
            {["Sunset", "Organic House", "Downtempo"].map((tag) => (
              <span key={tag} className="text-sm text-charcoal/50">{tag}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FeaturedMix() {
  const { t } = useLang();
  return (
    <section className="bg-sand/40 py-24 lg:py-32" data-testid="home-featured">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <Overline>{t("featured.overline")}</Overline>
            <h2 className="mt-5 max-w-xl font-serif text-4xl font-light leading-tight text-charcoal sm:text-5xl">
              {t("featured.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base text-charcoal/70">{t("featured.body")}</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <LazyEmbed
            type="soundcloud"
            url={EMBEDS.soundcloudUrl}
            poster={IMAGES.moodTexture}
            title="Featured SoundCloud mix"
            testId="home-soundcloud"
          />
        </Reveal>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { t, pathFor } = useLang();
  const items = t("servicesPreview.items");
  return (
    <section className="bg-cream py-24 lg:py-32" data-testid="home-services">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <Reveal>
            <Overline>{t("servicesPreview.overline")}</Overline>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-charcoal sm:text-5xl">
              {t("servicesPreview.title")}
            </h2>
          </Reveal>
        </div>
        <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.08} className="bg-cream">
              <div className="group h-full p-8 transition-colors duration-500 hover:bg-sand/40 lg:p-12" data-testid={`service-preview-${i}`}>
                <span className="font-serif text-5xl font-light text-terracotta/40">0{i + 1}</span>
                <h3 className="mt-6 font-serif text-3xl text-charcoal">{it.t}</h3>
                <p className="mt-3 text-base leading-relaxed text-charcoal/65">{it.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <Link to={pathFor("/services")} data-testid="services-all-link" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-terracotta">
            {t("cta.viewAll")} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function LiveExperience() {
  const { t } = useLang();
  return (
    <section className="bg-charcoal py-24 lg:py-32" data-testid="home-live">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="mb-12 max-w-xl">
          <Reveal>
            <Overline className="text-gold">{t("live.overline")}</Overline>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-cream sm:text-5xl">{t("live.title")}</h2>
            <p className="mt-5 text-base text-cream/70">{t("live.body")}</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <LazyEmbed type="youtube" id={EMBEDS.youtubeId} poster={IMAGES.gallery[3].src} title="Live experience video" testId="home-youtube" />
        </Reveal>
      </div>
    </section>
  );
}

function EventsPreview() {
  const { t, lang, pathFor } = useLang();
  const upcoming = upcomingEvents(eventsData.upcoming).slice(0, 3);
  const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <section className="bg-cream py-24 lg:py-32" data-testid="home-events">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-12 flex items-end justify-between">
          <Reveal>
            <Overline>{t("eventsPreview.overline")}</Overline>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-charcoal sm:text-5xl">{t("eventsPreview.title")}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to={pathFor("/events")} data-testid="events-all-link" className="hidden items-center gap-2 text-sm font-semibold text-terracotta sm:inline-flex">
              {t("cta.viewAll")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="border-t border-border">
          {upcoming.length ? upcoming.map((evt, i) => (
              <Reveal key={evt.id} delay={i * 0.06}>
                <div className="grid grid-cols-1 items-center gap-2 border-b border-border py-6 sm:grid-cols-[140px_1fr]" data-testid={`event-preview-${evt.id}`}>
                  <span className="text-sm font-semibold uppercase tracking-widest text-terracotta">{fmt(evt.date)}</span>
                  <div>
                    <p className="font-serif text-2xl text-charcoal">{evt.title[lang]}</p>
                    <p className="text-sm text-charcoal/55">{evt.venue[lang]} · {evt.city[lang]}</p>
                  </div>
                </div>
              </Reveal>
            )) : <p className="py-8 text-charcoal/50" data-testid="home-events-empty">{t("eventsPreview.empty")}</p>}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  return (
    <>
      <Hero />
      <EditorialMarquee items={t("marquee")} />
      <Intro />
      <FeaturedMix />
      <ServicesPreview />
      <LiveExperience />
      <EventsPreview />
      <FinalCTA />
    </>
  );
}
